/**
 * One timestamp format for the whole product: Pacific time, written the
 * reader's way.
 *
 * TWO separate decisions live here, and they are deliberately not the same
 * decision:
 *
 * 1. The ZONE is fixed. Every figure on this dashboard describes a Cloudflare
 *    window the backend computed in `America/Los_Angeles`, and the team reads
 *    one clock — a timestamp that silently followed the viewer's machine would
 *    make two people looking at the same incident disagree about when it
 *    started.
 * 2. The WORDING follows the language: "Aug 30, 2026, 11:00 AM" or
 *    "2026年8月30日 11:00". `toLocaleString()` with no locale argument follows
 *    the BROWSER instead, which is how the invite-expiry line ended up printing
 *    a US date inside a Chinese sentence on an en-US machine.
 *
 * It is a plain module rather than a hook so both a component and a test can
 * ask for an explicit language, and it lives in `i18n/` rather than beside any
 * one screen because the invite panel (account) and the freshness badge
 * (dashboard) are two areas that must not each own half of this.
 */
import type { Lang } from './runtime'

/** The zone the backend computes in, and the only zone this app prints. */
export const DISPLAY_TIME_ZONE = 'America/Los_Angeles'

/** BCP 47 tags for `toLocaleString`. Not the same strings as our `Lang` ids:
 *  'zh' alone would leave the calendar and numbering to the browser. */
export const DATE_LOCALE: Record<Lang, string> = { en: 'en-US', zh: 'zh-CN' }

/**
 * An ISO timestamp as a Pacific wall-clock time in `lang`, or `null` if it
 * cannot be parsed.
 *
 * `toLocaleString` on an invalid Date yields the literal string "Invalid Date",
 * which looks like a value. `null` lets the caller say it does not know.
 */
export function formatPacific(iso: string, lang: Lang): string | null {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString(DATE_LOCALE[lang], {
    timeZone: DISPLAY_TIME_ZONE,
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}
